import React, { useEffect, useState } from 'react';
import '../../assets/styles/AuthForm.css';
import { validateForgotPasswordForm } from '../../utils/validators';
import { ValidationErrors } from '../../types/auth.types';
import { authService } from '../../services/authService';
import axios, { AxiosError } from "axios";
import Footer from '../../components/Layout/Footer';
import { useNavigate } from 'react-router-dom';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});

  const handleBlur = (field: string) => {
    setTouched({ ...touched, [field]: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = validateForgotPasswordForm(email);
    setValidationErrors(errors);

    if (Object.keys(errors).length !== 0) return;

    setLoading(true);
    setError(null);

    try {
      // 🔹 Replace with real API call
      const response = await authService.forgotPassword({ email });
      if (response?.data?.status === 'failed') {
        throw new Error(response.data.message || 'Failed to send reset email');
        return;
      }
      setEmailSent(true);

    } catch (err) {
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as any;

        const message =
          data?.message ||
          data?.details?.[0]?.msg ||
          "Something went wrong";

        setError(message);
      } else {
        setError("Unexpected error occurred");
      }
    } finally {
      setLoading(false);
      setEmail('');
    }
  };

  const renderFieldError = (field: string) =>
    touched[field] && validationErrors[field] ? (
      <span className="field-error">{validationErrors[field]}</span>
    ) : null;

  const getFieldClassName = (field: string) =>
    touched[field] && validationErrors[field]
      ? 'form-group has-error'
      : 'form-group';


  useEffect(() => {
    if (emailSent) {
      const timer = setTimeout(() => setEmailSent(false), 10000);
      return () => clearTimeout(timer);
    }
  }, [emailSent]);

  return (
    <>
      <div className="auth-form-container">
        <div className="auth-logos">
           <img src="/images/cc-spokesly-logo.png" alt="Main Logo"   style={{ height: "63px" }}/>
        </div>
        <div className="auth-form-wrapper">
          <form className="auth-form" onSubmit={handleSubmit}>
            <p className="forgot-password-description-header text-center">
              Forgot password
            </p>

            <p className="forgot-password-description">
              Please enter your registered email address below, and we will send you a link to reset your password.
            </p>

            <div className={getFieldClassName('email')}>
              <label htmlFor="email">Email Address</label>
              <input
                type="text"
                id="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => handleBlur('email')}
                required
              />
              {renderFieldError('email')}
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              disabled={loading || !email.trim()}
              className="submit-button"
            >
              {loading ? 'Loading...' : 'Reset password'}
            </button>
          </form>
        </div>
        {emailSent && (
          <div className="toast-overlay">
            <div className="toast-box">
              <div className="toast-icon">
                ✓
              </div>
              <p className="toast-text">Email sent successfully.</p>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default ForgotPasswordPage;
